/**
 * Float Plan Screen
 * 
 * Create and manage float plans before starting a trip.
 * Safety documentation with destination, waypoints, expected return, and notes.
 */
import React, { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileText, MapPin, Clock, Anchor, Ship, Plus, Trash2, 
  Share2, Copy, X, Check, Navigation, AlertTriangle, Crown, Lock,
  ChevronDown, ChevronUp, Radio, Users, Shield, Fuel
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import PremiumUpgradeModal from './PremiumUpgrade';
import { Checkbox } from '@/components/ui/checkbox';

const FloatPlanScreen = () => {
  const { activeTrip, startTrip, myLocation, isPaid, upgradeSubscription, user } = useApp();
  
  // Form state - Trip Details
  const [destination, setDestination] = useState('');
  const [destinationLat, setDestinationLat] = useState('');
  const [destinationLon, setDestinationLon] = useState('');
  const [departurePoint, setDeparturePoint] = useState('');
  const [departureLat, setDepartureLat] = useState('');
  const [departureLon, setDepartureLon] = useState('');
  const [waypoints, setWaypoints] = useState([]);
  const [expectedDeparture, setExpectedDeparture] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [notes, setNotes] = useState('');
  
  // Vessel Information (CG-3532 Section 1)
  const [vesselName, setVesselName] = useState('');
  const [vesselType, setVesselType] = useState('');
  const [vesselLength, setVesselLength] = useState('');
  const [vesselDraft, setVesselDraft] = useState('');
  const [vesselColor, setVesselColor] = useState('');
  const [vesselRegistration, setVesselRegistration] = useState('');
  const [hullMaterial, setHullMaterial] = useState('');
  const [fuelCapacity, setFuelCapacity] = useState('');
  const [engineType, setEngineType] = useState('');
  const [vesselInfo, setVesselInfo] = useState(''); // Additional info
  
  // Communications (CG-3532 Section 2)
  const [vhfChannels, setVhfChannels] = useState('16');
  const [mmsiNumber, setMmsiNumber] = useState('');
  const [cellPhone, setCellPhone] = useState('');
  const [satPhone, setSatPhone] = useState('');
  
  // Safety Equipment (CG-3532 Section 4)
  const [pfdCount, setPfdCount] = useState('');
  const [hasFlares, setHasFlares] = useState(false);
  const [hasEpirb, setHasEpirb] = useState(false);
  const [hasFireExt, setHasFireExt] = useState(false);
  const [hasVhfRadio, setHasVhfRadio] = useState(false);
  const [hasGps, setHasGps] = useState(false);
  const [hasAnchor, setHasAnchor] = useState(false);
  const [safetyEquipment, setSafetyEquipment] = useState('');
  
  // Persons on Board (CG-3532 Section 3)
  const [personsOnboard, setPersonsOnboard] = useState('');
  const [passengersInfo, setPassengersInfo] = useState('');
  
  // Emergency Contacts
  const [emergencyContacts, setEmergencyContacts] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planHistory, setPlanHistory] = useState([]);
  const [shareUrl, setShareUrl] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showVesselDetails, setShowVesselDetails] = useState(false);
  const [showSafetyEquipment, setShowSafetyEquipment] = useState(false);
  
  // Load draft plan on mount (only for paid users)
  useEffect(() => {
    if (isPaid) {
      loadDraftPlan();
      loadPlanHistory();
    }
  }, [isPaid]);
  
  // Premium gate for free users
  if (!isPaid) {
    return (
      <div className="p-4 flex flex-col items-center justify-center min-h-[60vh]" data-testid="float-plan-screen">
        <PremiumUpgradeModal 
          isOpen={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          onUpgrade={async () => {
            await upgradeSubscription();
            setShowPremiumModal(false);
          }}
          blockedFeature="Float Plans"
        />
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-10 h-10 text-cyan-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Float Plans</h2>
          <p className="text-gray-600 mb-6">
            Create detailed float plans before your trip. Share them with friends and family so they know your route and expected return time.
          </p>
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-cyan-800 flex items-center gap-2 justify-center">
              <Crown className="w-4 h-4" />
              This feature requires Boat 360 Plus
            </p>
          </div>
          <Button 
            onClick={() => setShowPremiumModal(true)}
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600"
            data-testid="upgrade-plan-btn"
          >
            <Crown className="w-4 h-4 mr-2" />
            Upgrade to Plus
          </Button>
        </div>
      </div>
    );
  }
  
  const loadDraftPlan = async () => {
    try {
      const response = await api.get('/float-plans/draft');
      if (response.data) {
        const plan = response.data;
        setCurrentPlan(plan);
        
        // Trip details
        setDestination(plan.destination || '');
        setDestinationLat(plan.destination_lat?.toString() || '');
        setDestinationLon(plan.destination_lon?.toString() || '');
        setDeparturePoint(plan.departure_point || '');
        setDepartureLat(plan.departure_lat?.toString() || '');
        setDepartureLon(plan.departure_lon?.toString() || '');
        setWaypoints(plan.waypoints || []);
        setExpectedDeparture(plan.expected_departure_at?.substring(0, 16) || '');
        setExpectedReturn(plan.expected_return_at?.substring(0, 16) || '');
        setNotes(plan.notes || '');
        
        // Vessel info
        setVesselName(plan.vessel_name || '');
        setVesselType(plan.vessel_type || '');
        setVesselLength(plan.vessel_length_ft?.toString() || '');
        setVesselDraft(plan.vessel_draft_ft?.toString() || user?.boat_draft_ft?.toString() || '');
        setVesselColor(plan.vessel_color || '');
        setVesselRegistration(plan.vessel_registration || '');
        setHullMaterial(plan.hull_material || '');
        setFuelCapacity(plan.fuel_capacity_gal?.toString() || '');
        setEngineType(plan.engine_type || '');
        setVesselInfo(plan.vessel_info || '');
        
        // Communications
        setVhfChannels(plan.vhf_radio_channels || '16');
        setMmsiNumber(plan.mmsi_number || '');
        setCellPhone(plan.cell_phone || '');
        setSatPhone(plan.satellite_phone || '');
        
        // Safety equipment
        setPfdCount(plan.pfd_count?.toString() || '');
        setHasFlares(plan.flares_onboard || false);
        setHasEpirb(plan.epirb_onboard || false);
        setHasFireExt(plan.fire_extinguisher || false);
        setHasVhfRadio(plan.vhf_radio || false);
        setHasGps(plan.gps_onboard || false);
        setHasAnchor(plan.anchor_onboard || false);
        setSafetyEquipment(plan.safety_equipment || '');
        
        // Persons
        setPersonsOnboard(plan.persons_onboard?.toString() || '');
        setPassengersInfo(plan.passengers_info || '');
        
        // Emergency
        setEmergencyContacts(plan.emergency_contacts || '');
      }
    } catch (error) {
      // No draft plan exists
    }
  };
  
  const loadPlanHistory = async () => {
    try {
      const response = await api.get('/float-plans?limit=10');
      setPlanHistory(response.data || []);
    } catch (error) {
      console.error('Failed to load plan history:', error);
    }
  };
  
  const handleSavePlan = async () => {
    if (!destination.trim()) {
      toast.error('Please enter a destination');
      return;
    }
    
    setLoading(true);
    try {
      const planData = {
        // Trip details
        destination: destination.trim(),
        destination_lat: destinationLat ? parseFloat(destinationLat) : null,
        destination_lon: destinationLon ? parseFloat(destinationLon) : null,
        departure_point: departurePoint.trim() || null,
        departure_lat: departureLat ? parseFloat(departureLat) : null,
        departure_lon: departureLon ? parseFloat(departureLon) : null,
        waypoints: waypoints.map((wp, i) => ({
          type: wp.type || 'waypoint',
          name: wp.name || '',
          lat: wp.lat || null,
          lon: wp.lon || null,
          notes: wp.notes || null,
          order: i
        })),
        expected_departure_at: expectedDeparture ? new Date(expectedDeparture).toISOString() : null,
        expected_return_at: expectedReturn ? new Date(expectedReturn).toISOString() : null,
        notes: notes.trim() || null,
        
        // Vessel info
        vessel_name: vesselName.trim() || null,
        vessel_type: vesselType.trim() || null,
        vessel_length_ft: vesselLength ? parseFloat(vesselLength) : null,
        vessel_draft_ft: vesselDraft ? parseFloat(vesselDraft) : null,
        vessel_color: vesselColor.trim() || null,
        vessel_registration: vesselRegistration.trim() || null,
        hull_material: hullMaterial.trim() || null,
        fuel_capacity_gal: fuelCapacity ? parseFloat(fuelCapacity) : null,
        engine_type: engineType.trim() || null,
        vessel_info: vesselInfo.trim() || null,
        
        // Communications
        vhf_radio_channels: vhfChannels.trim() || null,
        mmsi_number: mmsiNumber.trim() || null,
        cell_phone: cellPhone.trim() || null,
        satellite_phone: satPhone.trim() || null,
        
        // Safety equipment
        pfd_count: pfdCount ? parseInt(pfdCount) : null,
        flares_onboard: hasFlares,
        epirb_onboard: hasEpirb,
        fire_extinguisher: hasFireExt,
        vhf_radio: hasVhfRadio,
        gps_onboard: hasGps,
        anchor_onboard: hasAnchor,
        safety_equipment: safetyEquipment.trim() || null,
        
        // Persons
        persons_onboard: personsOnboard ? parseInt(personsOnboard) : null,
        passengers_info: passengersInfo.trim() || null,
        
        // Emergency
        emergency_contacts: emergencyContacts.trim() || null
      };
      
      let response;
      if (currentPlan?.id) {
        response = await api.put(`/float-plans/${currentPlan.id}`, planData);
        toast.success('Float plan updated');
      } else {
        response = await api.post('/float-plans', planData);
        toast.success('Float plan created');
      }
      
      setCurrentPlan(response.data);
      loadPlanHistory();
    } catch (error) {
      toast.error('Failed to save float plan');
    } finally {
      setLoading(false);
    }
  };
  
  const handleStartTripWithPlan = async () => {
    if (!currentPlan?.id) {
      toast.error('Please save your float plan first');
      return;
    }
    
    setLoading(true);
    try {
      await startTrip({ float_plan_id: currentPlan.id });
      toast.success('Trip started with float plan');
    } catch (error) {
      toast.error('Failed to start trip');
    } finally {
      setLoading(false);
    }
  };
  
  const handleShare = async () => {
    if (!currentPlan?.id) {
      toast.error('Please save your float plan first');
      return;
    }
    
    try {
      const response = await api.post(`/float-plans/${currentPlan.id}/share/start`);
      const fullUrl = `${window.location.origin}${response.data.share_url}`;
      setShareUrl(fullUrl);
      navigator.clipboard.writeText(fullUrl);
      toast.success('Share link copied to clipboard');
    } catch (error) {
      toast.error('Failed to create share link');
    }
  };
  
  const handleStopSharing = async () => {
    if (!currentPlan?.id) return;
    
    try {
      await api.post(`/float-plans/${currentPlan.id}/share/stop`);
      setShareUrl('');
      toast.success('Sharing stopped');
    } catch (error) {
      toast.error('Failed to stop sharing');
    }
  };
  
  const handleExportPDF = async () => {
    if (!currentPlan?.id) {
      toast.error('Please save your float plan first');
      return;
    }
    
    try {
      setLoading(true);
      const response = await api.get(`/float-plans/${currentPlan.id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `float_plan_${destination.replace(/\s+/g, '_').slice(0, 20)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Float plan PDF downloaded');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setLoading(false);
    }
  };
  
  const addWaypoint = () => {
    if (waypoints.length >= 5) {
      toast.error('Maximum 5 waypoints allowed');
      return;
    }
    setWaypoints([...waypoints, { type: 'waypoint', name: '', lat: null, lon: null }]);
  };
  
  const removeWaypoint = (index) => {
    setWaypoints(waypoints.filter((_, i) => i !== index));
  };
  
  const updateWaypoint = (index, field, value) => {
    const updated = [...waypoints];
    updated[index] = { ...updated[index], [field]: value };
    setWaypoints(updated);
  };
  
  const clearForm = () => {
    setCurrentPlan(null);
    setDestination('');
    setDestinationLat('');
    setDestinationLon('');
    setDeparturePoint('');
    setDepartureLat('');
    setDepartureLon('');
    setWaypoints([]);
    setExpectedDeparture('');
    setExpectedReturn('');
    setNotes('');
    setVesselName('');
    setVesselType('');
    setVesselLength('');
    setVesselDraft(user?.boat_draft_ft?.toString() || '');
    setVesselColor('');
    setVesselRegistration('');
    setHullMaterial('');
    setFuelCapacity('');
    setEngineType('');
    setVesselInfo('');
    setVhfChannels('16');
    setMmsiNumber('');
    setCellPhone('');
    setSatPhone('');
    setPfdCount('');
    setHasFlares(false);
    setHasEpirb(false);
    setHasFireExt(false);
    setHasVhfRadio(false);
    setHasGps(false);
    setHasAnchor(false);
    setSafetyEquipment('');
    setPersonsOnboard('');
    setPassengersInfo('');
    setEmergencyContacts('');
    setShareUrl('');
  };
  
  const loadPlan = (plan) => {
    setCurrentPlan(plan);
    setDestination(plan.destination || '');
    setDestinationLat(plan.destination_lat?.toString() || '');
    setDestinationLon(plan.destination_lon?.toString() || '');
    setDeparturePoint(plan.departure_point || '');
    setDepartureLat(plan.departure_lat?.toString() || '');
    setDepartureLon(plan.departure_lon?.toString() || '');
    setWaypoints(plan.waypoints || []);
    setExpectedDeparture(plan.expected_departure_at?.substring(0, 16) || '');
    setExpectedReturn(plan.expected_return_at?.substring(0, 16) || '');
    setNotes(plan.notes || '');
    setVesselName(plan.vessel_name || '');
    setVesselType(plan.vessel_type || '');
    setVesselLength(plan.vessel_length_ft?.toString() || '');
    setVesselDraft(plan.vessel_draft_ft?.toString() || user?.boat_draft_ft?.toString() || '');
    setVesselColor(plan.vessel_color || '');
    setVesselRegistration(plan.vessel_registration || '');
    setHullMaterial(plan.hull_material || '');
    setFuelCapacity(plan.fuel_capacity_gal?.toString() || '');
    setEngineType(plan.engine_type || '');
    setVesselInfo(plan.vessel_info || '');
    setVhfChannels(plan.vhf_radio_channels || '16');
    setMmsiNumber(plan.mmsi_number || '');
    setCellPhone(plan.cell_phone || '');
    setSatPhone(plan.satellite_phone || '');
    setPfdCount(plan.pfd_count?.toString() || '');
    setHasFlares(plan.flares_onboard || false);
    setHasEpirb(plan.epirb_onboard || false);
    setHasFireExt(plan.fire_extinguisher || false);
    setHasVhfRadio(plan.vhf_radio || false);
    setHasGps(plan.gps_onboard || false);
    setHasAnchor(plan.anchor_onboard || false);
    setSafetyEquipment(plan.safety_equipment || '');
    setPersonsOnboard(plan.persons_onboard?.toString() || '');
    setPassengersInfo(plan.passengers_info || '');
    setEmergencyContacts(plan.emergency_contacts || '');
    setShowHistory(false);
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-500';
      case 'active': return 'bg-green-500';
      case 'completed': return 'bg-blue-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };
  
  if (activeTrip) {
    return (
      <div className="p-4 max-w-2xl mx-auto" data-testid="float-plan-screen">
        <Card className="bg-cyan-50 border-cyan-200">
          <CardContent className="p-6 text-center">
            <Ship className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-800 mb-2">Trip In Progress</h3>
            <p className="text-gray-600">
              You&apos;re currently on a trip. Float plans can only be created or edited before starting a trip.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="p-4 max-w-2xl mx-auto" data-testid="float-plan-screen">
      <ScrollArea className="h-[calc(100vh-8rem)]">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-cyan-600" />
            <h1 className="text-xl font-bold text-gray-800">Float Plan</h1>
            {currentPlan && (
              <Badge className={getStatusColor(currentPlan.status)}>
                {currentPlan.status}
              </Badge>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
          >
            {showHistory ? 'New Plan' : 'History'}
          </Button>
        </div>
        
        {showHistory ? (
          /* Plan History */
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Float Plans</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {planHistory.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No float plans yet</p>
              ) : (
                planHistory.map((plan) => (
                  <div
                    key={plan.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                    onClick={() => plan.status === 'draft' && loadPlan(plan)}
                  >
                    <div>
                      <p className="font-medium text-gray-800">{plan.destination}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(plan.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge className={getStatusColor(plan.status)}>{plan.status}</Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Destination */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-cyan-600" />
                  Destination
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label htmlFor="destination">Where are you going?</Label>
                  <Input
                    id="destination"
                    placeholder="e.g., Catalina Island"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    data-testid="destination-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="destLat">Latitude (optional)</Label>
                    <Input
                      id="destLat"
                      type="number"
                      step="any"
                      placeholder="33.4484"
                      value={destinationLat}
                      onChange={(e) => setDestinationLat(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="destLon">Longitude (optional)</Label>
                    <Input
                      id="destLon"
                      type="number"
                      step="any"
                      placeholder="-118.4883"
                      value={destinationLon}
                      onChange={(e) => setDestinationLon(e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Waypoints */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Navigation className="w-4 h-4 text-cyan-600" />
                    Waypoints ({waypoints.length}/5)
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={addWaypoint}
                    disabled={waypoints.length >= 5}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {waypoints.length === 0 ? (
                  <p className="text-gray-500 text-center py-2 text-sm">
                    No waypoints added. Add stops along your route.
                  </p>
                ) : (
                  waypoints.map((wp, index) => (
                    <div key={index} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder="Waypoint name"
                          value={wp.name || ''}
                          onChange={(e) => updateWaypoint(index, 'name', e.target.value)}
                          className="text-sm"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            type="number"
                            step="any"
                            placeholder="Lat"
                            value={wp.lat || ''}
                            onChange={(e) => updateWaypoint(index, 'lat', parseFloat(e.target.value) || null)}
                            className="text-sm"
                          />
                          <Input
                            type="number"
                            step="any"
                            placeholder="Lon"
                            value={wp.lon || ''}
                            onChange={(e) => updateWaypoint(index, 'lon', parseFloat(e.target.value) || null)}
                            className="text-sm"
                          />
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeWaypoint(index)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
            
            {/* Expected Return */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4 text-cyan-600" />
                  Departure & Return Times
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="expectedDeparture">Departure Time</Label>
                    <Input
                      id="expectedDeparture"
                      type="datetime-local"
                      value={expectedDeparture}
                      onChange={(e) => setExpectedDeparture(e.target.value)}
                      data-testid="expected-departure-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="expectedReturn">Expected Return</Label>
                    <Input
                      id="expectedReturn"
                      type="datetime-local"
                      value={expectedReturn}
                      onChange={(e) => setExpectedReturn(e.target.value)}
                      data-testid="expected-return-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Friends will be alerted if you are overdue
                </p>
              </CardContent>
            </Card>
            
            {/* Departure Point */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Anchor className="w-4 h-4 text-cyan-600" />
                  Departure Point
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label htmlFor="departurePoint">Where are you leaving from?</Label>
                  <Input
                    id="departurePoint"
                    placeholder="e.g., Marina del Rey Harbor"
                    value={departurePoint}
                    onChange={(e) => setDeparturePoint(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label htmlFor="depLat">Latitude (optional)</Label>
                    <Input
                      id="depLat"
                      type="number"
                      step="any"
                      placeholder="33.9802"
                      value={departureLat}
                      onChange={(e) => setDepartureLat(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="depLon">Longitude (optional)</Label>
                    <Input
                      id="depLon"
                      type="number"
                      step="any"
                      placeholder="-118.4517"
                      value={departureLon}
                      onChange={(e) => setDepartureLon(e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Vessel Information (Collapsible) */}
            <Card className="mb-4">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setShowVesselDetails(!showVesselDetails)}
              >
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Ship className="w-4 h-4 text-cyan-600" />
                    Vessel Information (CG-3532)
                  </span>
                  {showVesselDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </CardTitle>
              </CardHeader>
              {showVesselDetails && (
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Vessel Name</Label>
                      <Input
                        placeholder="My Boat"
                        value={vesselName}
                        onChange={(e) => setVesselName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Registration #</Label>
                      <Input
                        placeholder="FL 1234 AB"
                        value={vesselRegistration}
                        onChange={(e) => setVesselRegistration(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label>Type</Label>
                      <Input
                        placeholder="Powerboat"
                        value={vesselType}
                        onChange={(e) => setVesselType(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Length (ft)</Label>
                      <Input
                        type="number"
                        placeholder="24"
                        value={vesselLength}
                        onChange={(e) => setVesselLength(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Draft (ft)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="3.5"
                        value={vesselDraft}
                        onChange={(e) => setVesselDraft(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Color</Label>
                      <Input
                        placeholder="White/Blue"
                        value={vesselColor}
                        onChange={(e) => setVesselColor(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Hull Material</Label>
                      <Input
                        placeholder="Fiberglass"
                        value={hullMaterial}
                        onChange={(e) => setHullMaterial(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Engine Type</Label>
                      <Input
                        placeholder="Twin Outboard"
                        value={engineType}
                        onChange={(e) => setEngineType(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Fuel Capacity (gal)</Label>
                      <Input
                        type="number"
                        placeholder="100"
                        value={fuelCapacity}
                        onChange={(e) => setFuelCapacity(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Additional Info / Distinguishing Features</Label>
                    <Input
                      placeholder="T-top, radar arch, blue bimini..."
                      value={vesselInfo}
                      onChange={(e) => setVesselInfo(e.target.value)}
                    />
                  </div>
                </CardContent>
              )}
            </Card>
            
            {/* Communications */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Radio className="w-4 h-4 text-cyan-600" />
                  Communications
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>VHF Channels</Label>
                    <Input
                      placeholder="16, 22A, 68"
                      value={vhfChannels}
                      onChange={(e) => setVhfChannels(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>MMSI # (DSC)</Label>
                    <Input
                      placeholder="366123456"
                      value={mmsiNumber}
                      onChange={(e) => setMmsiNumber(e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Cell Phone</Label>
                    <Input
                      type="tel"
                      placeholder="555-123-4567"
                      value={cellPhone}
                      onChange={(e) => setCellPhone(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Satellite Phone (if any)</Label>
                    <Input
                      type="tel"
                      placeholder="Optional"
                      value={satPhone}
                      onChange={(e) => setSatPhone(e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Safety Equipment (Collapsible) */}
            <Card className="mb-4">
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setShowSafetyEquipment(!showSafetyEquipment)}
              >
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-cyan-600" />
                    Safety Equipment
                  </span>
                  {showSafetyEquipment ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </CardTitle>
              </CardHeader>
              {showSafetyEquipment && (
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label># of PFDs/Life Jackets</Label>
                      <Input
                        type="number"
                        placeholder="4"
                        value={pfdCount}
                        onChange={(e) => setPfdCount(e.target.value)}
                      />
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Checkbox id="hasFlares" checked={hasFlares} onCheckedChange={setHasFlares} />
                      <Label htmlFor="hasFlares" className="cursor-pointer">Visual Distress Signals/Flares</Label>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div className="flex items-center gap-2">
                      <Checkbox id="hasEpirb" checked={hasEpirb} onCheckedChange={setHasEpirb} />
                      <Label htmlFor="hasEpirb" className="cursor-pointer">EPIRB/PLB</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id="hasFireExt" checked={hasFireExt} onCheckedChange={setHasFireExt} />
                      <Label htmlFor="hasFireExt" className="cursor-pointer">Fire Extinguisher</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id="hasVhfRadio" checked={hasVhfRadio} onCheckedChange={setHasVhfRadio} />
                      <Label htmlFor="hasVhfRadio" className="cursor-pointer">VHF Radio</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id="hasGps" checked={hasGps} onCheckedChange={setHasGps} />
                      <Label htmlFor="hasGps" className="cursor-pointer">GPS/Chartplotter</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox id="hasAnchor" checked={hasAnchor} onCheckedChange={setHasAnchor} />
                      <Label htmlFor="hasAnchor" className="cursor-pointer">Anchor & Line</Label>
                    </div>
                  </div>
                  <div>
                    <Label>Other Equipment</Label>
                    <Input
                      placeholder="First aid kit, horn, throwable PFD..."
                      value={safetyEquipment}
                      onChange={(e) => setSafetyEquipment(e.target.value)}
                    />
                  </div>
                </CardContent>
              )}
            </Card>
            
            {/* Persons on Board */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-cyan-600" />
                  Persons on Board
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Total Number of Persons</Label>
                  <Input
                    type="number"
                    placeholder="4"
                    value={personsOnboard}
                    onChange={(e) => setPersonsOnboard(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Passengers (names, ages, medical conditions)</Label>
                  <Textarea
                    placeholder="John Doe (45), Jane Doe (42), Kids: Tom (12), Sue (8)"
                    value={passengersInfo}
                    onChange={(e) => setPassengersInfo(e.target.value)}
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Emergency Contacts */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  Emergency Contacts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Shore contact: John Smith 555-123-4567&#10;Marina: Newport Marina 555-987-6543"
                  value={emergencyContacts}
                  onChange={(e) => setEmergencyContacts(e.target.value)}
                  rows={2}
                />
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Leave this plan with a responsible person who can notify authorities if you&apos;re overdue
                </p>
              </CardContent>
            </Card>
            
            {/* Notes */}
            <Card className="mb-4">
              <CardHeader>
                <CardTitle className="text-base">Additional Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Any other details about your trip..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </CardContent>
            </Card>
            
            {/* Share */}
            {currentPlan?.id && (
              <Card className="mb-4">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-cyan-600" />
                    Share Float Plan
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {shareUrl ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 bg-gray-100 rounded p-2">
                        <span className="text-xs text-gray-600 truncate flex-1">{shareUrl}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            navigator.clipboard.writeText(shareUrl);
                            toast.success('Copied!');
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleStopSharing}
                        className="w-full text-red-600"
                      >
                        <X className="w-4 h-4 mr-1" /> Stop Sharing
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={handleShare}
                      className="w-full"
                      data-testid="share-plan-btn"
                    >
                      <Share2 className="w-4 h-4 mr-2" /> Create Share Link
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Actions */}
            <div className="space-y-2 mb-4">
              <Button
                onClick={handleSavePlan}
                disabled={loading || !destination.trim()}
                className="w-full bg-cyan-500 hover:bg-cyan-600"
                data-testid="save-plan-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : currentPlan?.id ? 'Update Float Plan' : 'Save Float Plan'}
              </Button>
              
              {currentPlan?.id && (
                <Button
                  onClick={handleStartTripWithPlan}
                  disabled={loading}
                  variant="outline"
                  className="w-full border-green-500 text-green-600 hover:bg-green-50"
                  data-testid="start-trip-btn"
                >
                  <Ship className="w-4 h-4 mr-2" />
                  Start Trip with This Plan
                </Button>
              )}
              
              {currentPlan?.id && (
                <Button
                  onClick={handleExportPDF}
                  disabled={loading}
                  variant="outline"
                  className="w-full"
                  data-testid="export-pdf-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download Coast Guard PDF
                </Button>
              )}
              
              <Button
                onClick={clearForm}
                variant="ghost"
                className="w-full text-gray-500"
              >
                Clear Form
              </Button>
            </div>
            
            {/* Safety Notice */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-3 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Safety Reminder</p>
                  <p className="text-amber-700">
                    Share your float plan with someone on shore who can alert authorities if you don&apos;t return as expected.
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </ScrollArea>
    </div>
  );
};

export default FloatPlanScreen;
